import { CalcDecimal, decimalString, moneyString } from "./decimal.js";
import {
  DM2016_POST_COMPENSATION_ROUNDING_POLICY_ID,
  DM2016_SERVICE_ROUNDING_POLICY_ID,
  DM2016_SUMMARY_ROUNDING_POLICY_IDS,
  MONEY_ROUNDING_MODE,
  roundedMoneyDecimal,
  type Dm2016SummaryRoundingPolicy,
} from "./rounding-policy.js";

export const DM2016_CALCULATION_VERSION = "0.5.1";

const P_BASE = new CalcDecimal("0.03");
const P_NUMERATOR = new CalcDecimal("10");
const P_EXPONENT = new CalcDecimal("0.4");
const MINIMUM_VALUE_FOR_P = new CalcDecimal("25000");

type CalcDecimalValue = InstanceType<typeof CalcDecimal>;

export type Dm2016QDefinition =
  | {
      readonly kind: "single";
      readonly q: string;
    }
  | {
      readonly kind: "progressive_bands";
      readonly interpolateActiveBoundedBand: boolean;
      readonly interpolatedQDecimalPlaces?: number;
      readonly bands: readonly Dm2016BandDefinition[];
    };

export interface Dm2016BandDefinition {
  readonly upperBound: string | null;
  /** Q trascritto dalla fonte ufficiale. */
  readonly q: string;
  /** Q operativo, presente solo quando una scelta interpretativa documentata diverge dalla fonte. */
  readonly qApplied?: string;
}

export interface Dm2016ServiceCalculationInput {
  readonly serviceCode: string;
  readonly valueV: string;
  readonly complexityG: string;
  readonly qDefinition: Dm2016QDefinition;
  readonly selector?:
    | { readonly kind: "work_value" }
    | {
        readonly kind: "population";
        readonly inhabitants: string;
      };
  readonly multiplier?: string;
  readonly parameterPMode?: "used_band_value" | "total_work_value";
}

export interface Dm2016BandCalculationResult {
  readonly bandIndex: number;
  readonly lowerBound: string;
  readonly upperBound: string | null;
  readonly selectorPortion: string;
  readonly valueV: string;
  readonly valueUsedForParameterP: string;
  readonly qPrinted: string;
  readonly qApplied: string;
  readonly parameterP: string;
  readonly compensationRaw: string;
  readonly compensationRounded: string;
}

export interface Dm2016ServiceCalculationResult {
  readonly calculationVersion: string;
  readonly moneyRoundingMode: typeof MONEY_ROUNDING_MODE;
  readonly roundingPolicyId: typeof DM2016_SERVICE_ROUNDING_POLICY_ID;
  readonly serviceCode: string;
  readonly valueV: string;
  readonly complexityG: string;
  readonly selectorKind: "work_value" | "population";
  readonly multiplier: string;
  readonly parameterPMode: "used_band_value" | "total_work_value";
  readonly bands: readonly Dm2016BandCalculationResult[];
  readonly compensationRaw: string;
  readonly compensationRounded: string;
}

export interface Dm2016CompensationSummaryInput {
  readonly serviceResults: readonly Dm2016ServiceCalculationResult[];
  readonly bimMandatory: boolean;
  readonly expensePercentage: string;
  readonly roundingPolicy?: Dm2016SummaryRoundingPolicy;
}

export interface Dm2016CompensationSummaryResult {
  readonly moneyRoundingMode: typeof MONEY_ROUNDING_MODE;
  readonly roundingPolicy: Dm2016SummaryRoundingPolicy;
  readonly roundingPolicyId:
    (typeof DM2016_SUMMARY_ROUNDING_POLICY_IDS)[Dm2016SummaryRoundingPolicy];
  readonly postCompensationRoundingPolicyId:
    typeof DM2016_POST_COMPENSATION_ROUNDING_POLICY_ID;
  readonly compensationRaw: string;
  readonly compensationRawRounded: string;
  readonly compensationRounded: string;
  readonly compensationRoundingDelta: string;
  readonly bimIncrementRaw: string;
  readonly bimIncrementRounded: string;
  readonly compensationAfterBimRaw: string;
  readonly compensationAfterBimRounded: string;
  readonly expensesRaw: string;
  readonly expensesRounded: string;
  readonly totalRaw: string;
  readonly totalRounded: string;
}

export class Dm2016CalculationInputError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "Dm2016CalculationInputError";
  }
}

function positiveDecimal(value: string, label: string): CalcDecimalValue {
  if (!/^(?:0|[1-9]\d*)(?:\.\d+)?$/.test(value)) {
    throw new Dm2016CalculationInputError(`${label} must be a plain decimal string`);
  }
  const result = new CalcDecimal(value);
  if (!result.isFinite() || result.lte(0)) {
    throw new Dm2016CalculationInputError(`${label} must be greater than zero`);
  }
  return result;
}

function nonNegativeDecimal(value: string, label: string): CalcDecimalValue {
  if (!/^(?:0|[1-9]\d*)(?:\.\d+)?$/.test(value)) {
    throw new Dm2016CalculationInputError(`${label} must be a plain decimal string`);
  }
  const result = new CalcDecimal(value);
  if (!result.isFinite() || result.lt(0)) {
    throw new Dm2016CalculationInputError(`${label} must not be negative`);
  }
  return result;
}

export function dm2016ParameterP(valueV: string): string {
  const value = positiveDecimal(valueV, "V");
  const effectiveValue = CalcDecimal.max(value, MINIMUM_VALUE_FOR_P);
  return decimalString(
    P_BASE.plus(P_NUMERATOR.div(effectiveValue.pow(P_EXPONENT))),
  );
}

function parameterP(value: CalcDecimalValue): CalcDecimalValue {
  const effectiveValue = CalcDecimal.max(value, MINIMUM_VALUE_FOR_P);
  return P_BASE.plus(P_NUMERATOR.div(effectiveValue.pow(P_EXPONENT)));
}

function freezeDeep<T>(value: T): Readonly<T> {
  if (value !== null && typeof value === "object" && !Object.isFrozen(value)) {
    Object.freeze(value);
    Object.values(value).forEach((child) => freezeDeep(child));
  }
  return value;
}

function calculateSingle(
  input: Dm2016ServiceCalculationInput,
  valueV: CalcDecimalValue,
  complexityG: CalcDecimalValue,
  multiplier: CalcDecimalValue,
  parameterPMode: "used_band_value" | "total_work_value",
): Dm2016ServiceCalculationResult {
  if (input.qDefinition.kind !== "single") {
    throw new Dm2016CalculationInputError("Expected a single Q definition");
  }
  const q = positiveDecimal(input.qDefinition.q, "Q");
  const pInput = valueV;
  const p = parameterP(pInput);
  const raw = valueV.times(complexityG).times(q).times(p).times(multiplier);
  const band: Dm2016BandCalculationResult = {
    bandIndex: 0,
    lowerBound: "0",
    upperBound: null,
    selectorPortion: decimalString(valueV),
    valueV: decimalString(valueV),
    valueUsedForParameterP: decimalString(CalcDecimal.max(pInput, MINIMUM_VALUE_FOR_P)),
    qPrinted: decimalString(q),
    qApplied: decimalString(q),
    parameterP: decimalString(p),
    compensationRaw: decimalString(raw),
    compensationRounded: moneyString(raw),
  };
  return freezeDeep({
    calculationVersion: DM2016_CALCULATION_VERSION,
    moneyRoundingMode: MONEY_ROUNDING_MODE,
    roundingPolicyId: DM2016_SERVICE_ROUNDING_POLICY_ID,
    serviceCode: input.serviceCode,
    valueV: decimalString(valueV),
    complexityG: decimalString(complexityG),
    selectorKind: input.selector?.kind ?? "work_value",
    multiplier: decimalString(multiplier),
    parameterPMode,
    bands: [band],
    compensationRaw: decimalString(raw),
    compensationRounded: moneyString(raw),
  });
}

export function calculateDm2016Service(
  input: Dm2016ServiceCalculationInput,
): Dm2016ServiceCalculationResult {
  if (!/^[A-Za-z][A-Za-z0-9.]*$/.test(input.serviceCode)) {
    throw new Dm2016CalculationInputError("Invalid service code");
  }
  const valueV = positiveDecimal(input.valueV, "V");
  const complexityG = positiveDecimal(input.complexityG, "G");
  const multiplier = positiveDecimal(input.multiplier ?? "1", "Multiplier");
  const parameterPMode = input.parameterPMode ?? "used_band_value";

  if (input.qDefinition.kind === "single") {
    if (input.selector?.kind === "population") {
      throw new Dm2016CalculationInputError(
        "Population selection requires progressive bands",
      );
    }
    return calculateSingle(
      input,
      valueV,
      complexityG,
      multiplier,
      parameterPMode,
    );
  }

  if (input.qDefinition.bands.length < 2) {
    throw new Dm2016CalculationInputError(
      "Progressive calculations require at least two bands",
    );
  }
  if (input.qDefinition.bands.at(-1)?.upperBound !== null) {
    throw new Dm2016CalculationInputError(
      "The last progressive band must be unbounded",
    );
  }

  const parsedBands = input.qDefinition.bands.map((band, index) => ({
    index,
    upperBound:
      band.upperBound === null
        ? null
        : positiveDecimal(band.upperBound, `Band ${index + 1} upper bound`),
    q: positiveDecimal(band.q, `Band ${index + 1} printed Q`),
    qApplied: positiveDecimal(
      band.qApplied ?? band.q,
      `Band ${index + 1} applied Q`,
    ),
  }));
  const interpolatedQDecimalPlaces = input.qDefinition.interpolatedQDecimalPlaces;
  if (
    interpolatedQDecimalPlaces !== undefined
    && (
      !Number.isInteger(interpolatedQDecimalPlaces)
      || interpolatedQDecimalPlaces < 0
      || interpolatedQDecimalPlaces > 12
    )
  ) {
    throw new Dm2016CalculationInputError(
      "Interpolated Q decimal places must be an integer between 0 and 12",
    );
  }
  let previousUpper = new CalcDecimal(0);
  parsedBands.forEach((band, index) => {
    if (band.upperBound === null && index !== parsedBands.length - 1) {
      throw new Dm2016CalculationInputError("Only the last band may be unbounded");
    }
    if (band.upperBound !== null && band.upperBound.lte(previousUpper)) {
      throw new Dm2016CalculationInputError(
        "Band upper bounds must be strictly increasing",
      );
    }
    if (band.upperBound !== null) previousUpper = band.upperBound;
  });

  const selector = input.selector ?? { kind: "work_value" as const };
  const selectorKind = selector.kind;
  const selectorTotal = selector.kind === "population"
    ? positiveDecimal(selector.inhabitants, "Inhabitants")
    : valueV;
  const valuePerSelectorUnit = selectorKind === "population"
    ? valueV.div(selectorTotal)
    : new CalcDecimal(1);

  const results: Dm2016BandCalculationResult[] = [];
  let lowerBound = new CalcDecimal(0);
  let previousQ: CalcDecimalValue | null = null;

  for (const band of parsedBands) {
    const remaining = selectorTotal.minus(lowerBound);
    if (remaining.lte(0)) break;
    const capacity = band.upperBound === null
      ? remaining
      : band.upperBound.minus(lowerBound);
    const selectorPortion = CalcDecimal.min(remaining, capacity);
    const bandValueV = selectorPortion.times(valuePerSelectorUnit);
    const isPartialBoundedBand =
      band.upperBound !== null && selectorTotal.lt(band.upperBound);
    let qApplied = band.qApplied;

    if (
      selectorKind === "work_value"
      && input.qDefinition.interpolateActiveBoundedBand
      && isPartialBoundedBand
      && band.upperBound !== null
      && previousQ !== null
    ) {
      const fraction = selectorPortion.div(band.upperBound.minus(lowerBound));
      qApplied = previousQ.plus(band.qApplied.minus(previousQ).times(fraction));
      if (interpolatedQDecimalPlaces !== undefined) {
        qApplied = qApplied.toDecimalPlaces(
          interpolatedQDecimalPlaces,
          CalcDecimal.ROUND_HALF_UP,
        );
      }
    }

    const pInput = parameterPMode === "total_work_value" ? valueV : bandValueV;
    const p = parameterP(pInput);
    const raw = bandValueV
      .times(complexityG)
      .times(qApplied)
      .times(p)
      .times(multiplier);
    results.push({
      bandIndex: band.index,
      lowerBound: decimalString(lowerBound),
      upperBound: band.upperBound === null ? null : decimalString(band.upperBound),
      selectorPortion: decimalString(selectorPortion),
      valueV: decimalString(bandValueV),
      valueUsedForParameterP: decimalString(CalcDecimal.max(pInput, MINIMUM_VALUE_FOR_P)),
      qPrinted: decimalString(band.q),
      qApplied: decimalString(qApplied),
      parameterP: decimalString(p),
      compensationRaw: decimalString(raw),
      compensationRounded: moneyString(raw),
    });
    previousQ = band.qApplied;
    if (band.upperBound === null) break;
    lowerBound = band.upperBound;
  }

  const compensationRaw = results.reduce(
    (total, band) => total.plus(band.compensationRaw),
    new CalcDecimal(0),
  );
  return freezeDeep({
    calculationVersion: DM2016_CALCULATION_VERSION,
    moneyRoundingMode: MONEY_ROUNDING_MODE,
    roundingPolicyId: DM2016_SERVICE_ROUNDING_POLICY_ID,
    serviceCode: input.serviceCode,
    valueV: decimalString(valueV),
    complexityG: decimalString(complexityG),
    selectorKind,
    multiplier: decimalString(multiplier),
    parameterPMode,
    bands: results,
    compensationRaw: decimalString(compensationRaw),
    compensationRounded: moneyString(compensationRaw),
  });
}

export function summarizeDm2016Compensation(
  input: Dm2016CompensationSummaryInput,
): Dm2016CompensationSummaryResult {
  if (input.serviceResults.length === 0) {
    throw new Dm2016CalculationInputError("At least one service result is required");
  }
  const expensePercentage = nonNegativeDecimal(
    input.expensePercentage,
    "Expense percentage",
  );
  if (expensePercentage.gt(100)) {
    throw new Dm2016CalculationInputError(
      "Expense percentage must not exceed 100",
    );
  }
  const compensationRaw = input.serviceResults.reduce(
    (total, result) => total.plus(result.compensationRaw),
    new CalcDecimal(0),
  );
  const roundingPolicy = input.roundingPolicy ?? "aggregate_raw";
  const compensationBasis = roundingPolicy === "sum_rounded_lines"
    ? input.serviceResults.reduce(
        (total, result) => total.plus(result.compensationRounded),
        new CalcDecimal(0),
      )
    : compensationRaw;
  const compensationRawRounded = roundedMoneyDecimal(compensationRaw);
  const compensationRounded = roundedMoneyDecimal(compensationBasis);
  const bimIncrementRaw = input.bimMandatory
    ? compensationRounded.times("0.10")
    : new CalcDecimal(0);
  const bimIncrementRounded = roundedMoneyDecimal(bimIncrementRaw);
  const compensationAfterBimRaw = compensationRounded.plus(bimIncrementRaw);
  const compensationAfterBimRounded = compensationRounded.plus(bimIncrementRounded);
  const expensesRaw = compensationAfterBimRounded.times(expensePercentage).div(100);
  const expensesRounded = roundedMoneyDecimal(expensesRaw);
  const totalRaw = compensationAfterBimRounded.plus(expensesRaw);
  const totalRounded = compensationAfterBimRounded.plus(expensesRounded);
  return freezeDeep({
    moneyRoundingMode: MONEY_ROUNDING_MODE,
    roundingPolicy,
    roundingPolicyId: DM2016_SUMMARY_ROUNDING_POLICY_IDS[roundingPolicy],
    postCompensationRoundingPolicyId:
      DM2016_POST_COMPENSATION_ROUNDING_POLICY_ID,
    compensationRaw: decimalString(compensationRaw),
    compensationRawRounded: moneyString(compensationRawRounded),
    compensationRounded: moneyString(compensationRounded),
    compensationRoundingDelta: moneyString(
      compensationRounded.minus(compensationRawRounded),
    ),
    bimIncrementRaw: decimalString(bimIncrementRaw),
    bimIncrementRounded: moneyString(bimIncrementRounded),
    compensationAfterBimRaw: decimalString(compensationAfterBimRaw),
    compensationAfterBimRounded: moneyString(compensationAfterBimRounded),
    expensesRaw: decimalString(expensesRaw),
    expensesRounded: moneyString(expensesRounded),
    totalRaw: decimalString(totalRaw),
    totalRounded: moneyString(totalRounded),
  });
}
